import React, { useState, useEffect } from "react";
import { message, Modal, Select, DatePicker, TimePicker, Input, Row, Col, Divider, Space, Button, Steps, Form, Progress, App } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { createBooking } from "../services/BookingService";
import { getBranches } from "../services/BranchService";
import { getServiceTitles } from "../services/ServiceService";
import { User, Phone, Car, Wrench, Calendar, Clock, Info, XCircle, ChevronRight, ChevronLeft, Building2, Sparkles, MapPin, CheckCircle2 } from "lucide-react";
import { normalizeImageUrl } from "../utils/imageUtils";
import BookingSuccessModal from "./BookingSuccessModal";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('Asia/Ho_Chi_Minh');

const { Option } = Select;
const { TextArea } = Input;
const CAR_BRANDS = ["Toyota", "Honda", "Ford", "Mazda", "Hyundai", "Kia", "VinFast", "Mercedes", "BMW", "Audi", "Mitsubishi", "Suzuki", "Nissan", "Khác"];
const isValidPhone = (p) => /^(0[3|5|7|8|9])[0-9]{8}$/.test(p);

const BookingModal = ({ isOpen, onClose }) => {
    const { modal, message: msgApi } = App.useApp();
    const [step, setStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();
    const [data, setData] = useState({
        name: "", phone: "", carBrand: "", carYear: "", carModel: "", carName: "",
        garageId: null, branchId: null, serviceId: null,
        scheduleDate: null, scheduleTime: null, note: ""
    });
    const [garages, setGarages] = useState([]);
    const [services, setServices] = useState([]);
    const [branches, setBranches] = useState([]);
    const [showSuccess, setShowSuccess] = useState(false);
    const [lastBooking, setLastBooking] = useState(null);
    const [formError, setFormError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setStep(0);
            form.resetFields();
            setData({ name: "", phone: "", carBrand: "", carYear: "", carModel: "", carName: "", garageId: null, branchId: null, serviceId: null, scheduleDate: null, scheduleTime: null, note: "" });

            const loadInitialData = async () => {
                try {
                    const [bRes, sRes] = await Promise.all([getBranches(), getServiceTitles()]);
                    let bData = Array.isArray(bRes) ? bRes : (bRes?.content || bRes?.items || []);
                    const branchesList = bData.map(b => ({
                        id: b._id || b.id,
                        name: b.name,
                        address: (b.addressObj?.street || b.address || ''),
                        imageUrl: b.imageUrl || ''
                    }));
                    setGarages([{ id: 1, name: "Trung Anh Garage", branches: branchesList }]);
                    const sList = Array.isArray(sRes) ? sRes : (sRes?.items || []);
                    setServices(sList.filter(s => s.isActive !== false).map(s => ({ id: s._id || s.id, name: s.title })));
                } catch (e) { console.error(e); }
            };
            loadInitialData();
        }
    }, [isOpen, form]);

    useEffect(() => {
        if (data.garageId) {
            const g = garages.find(g => g.id === data.garageId);
            setBranches(g ? g.branches : []);
        } else {
            setBranches([]);
            setData(p => ({ ...p, branchId: null }));
        }
    }, [data.garageId, garages]);

    const setField = (k, v) => { setData(p => ({ ...p, [k]: v })); };

    const handleNext = async () => {
        try {
            setFormError(null);
            await form.validateFields(['name', 'phone', 'carBrand', 'carYear', 'carModel', 'carName']);
            if (!isValidPhone(data.phone)) {
                setFormError("Số điện thoại không hợp lệ (VD: 0912345678)");
                return;
            }
            setStep(1);
        } catch (e) {
            setFormError("Vui lòng hoàn tất TẤT CẢ thông tin bắt buộc!");
            console.warn(e);
        }
    };

    const handleSubmit = async () => {
        try {
            setFormError(null);
            await form.validateFields(['garageId', 'branchId', 'serviceId', 'scheduleDate', 'scheduleTime']);
            setSubmitting(true);
            const garage = garages.find(g => g.id === data.garageId);
            const branch = branches.find(b => b.id === data.branchId);
            const service = services.find(s => s.id === data.serviceId);

            const payload = {
                name: data.name, phone: data.phone,
                carBrand: data.carBrand, carYear: data.carYear, carModel: data.carModel, carName: data.carName,
                garage: garage?.name || "", branch: branch?.name || "", branchAddress: branch?.address || "",
                serviceType: service?.name || "",
                scheduleAt: dayjs.tz(`${data.scheduleDate.format('YYYY-MM-DD')} ${data.scheduleTime}`, 'YYYY-MM-DD HH:mm', 'Asia/Ho_Chi_Minh').toISOString(),
                note: data.note, status: "PENDING"
            };
            await createBooking(payload);

            setLastBooking({
                ...payload,
                dateDisplay: data.scheduleDate.format('DD/MM/YYYY'),
                timeDisplay: data.scheduleTime
            });
            setShowSuccess(true);

            onClose();
        } catch (error) {
            if (error.response && error.response.data && (error.response.data.error || typeof error.response.data === 'string')) {
                const errorMsg = error.response.data.error || (typeof error.response.data === 'string' ? error.response.data : "Lỗi không xác định");
                setFormError(errorMsg);
            } else if (error.errorFields) {
                setFormError("Vui lòng hoàn tất TẤT CẢ thông tin bắt buộc!");
            } else {
                setFormError("Có lỗi xảy ra khi đặt lịch. Vui lòng thử lại!");
            }
            console.error("Booking error:", error);
        } finally {
            setSubmitting(false);
        }
    };

    const calculateProgress = () => {
        if (step === 0) {
            const fields = [data.phone, data.name, data.carBrand, data.carYear, data.carModel, data.carName];
            const filled = fields.filter(f => f && String(f).trim() !== "").length;
            return Math.round((filled / fields.length) * 100);
        } else {
            const fields = [data.garageId, data.branchId, data.serviceId, data.scheduleDate, data.scheduleTime];
            const filled = fields.filter(f => f !== null && String(f).trim() !== "").length;
            return Math.round((filled / fields.length) * 100);
        }
    };

    return (
        <>
            <Modal
                open={isOpen} onCancel={onClose} footer={null} width={750} centered title={null}
                bodyStyle={{ padding: 0, borderRadius: 24, overflow: 'hidden' }}
                closeIcon={<XCircle size={24} color="#fff" style={{ marginTop: 14, marginRight: 14 }} />}
            >
                <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #b91c1c 100%)", padding: "30px 40px", color: '#fff' }}>
                    <h4 style={{ margin: 0, fontWeight: 900, fontSize: 18, letterSpacing: '0.5px' }}>ĐẶT LỊCH DỊCH VỤ TRUNG ANH GARAGE</h4>
                    <div style={{ marginTop: 15 }}>
                        <Steps size="small" current={step} items={[{ title: 'Khách & Xe' }, { title: 'Dịch vụ' }]} style={{ padding: '6px', background: 'rgba(255,255,255,0.1)', borderRadius: 10 }} />
                    </div>
                </div>

                <div style={{ padding: "30px 40px", background: '#fff' }}>
                    <Form form={form} layout="vertical" colon={false}>
                        {step === 0 ? (
                            <div className="animate-in fade-in duration-400">
                                <Divider orientation="left" plain style={{ margin: "0 0 20px" }}><Space><User size={16} color="#3b82f6" /><span style={{ fontSize: 12, fontWeight: 800, color: '#475569' }}>THÔNG TIN CHỦ XE (TẤT CẢ BẮT BUỘC)</span></Space></Divider>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item name="phone" label={<span style={{ fontWeight: 600, fontSize: 13 }}><Phone size={14} color="#10b981" style={{ marginRight: 8 }} />Số điện thoại</span>} rules={[{ required: true, message: 'Nhập SĐT' }]} required>
                                            <Input placeholder="09xxx..." value={data.phone} onChange={e => setField("phone", e.target.value)} size="large" maxLength={11} style={{ borderRadius: 10 }} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item name="name" label={<span style={{ fontWeight: 600, fontSize: 13 }}><User size={14} color="#6366f1" style={{ marginRight: 8 }} />Họ và tên</span>} rules={[{ required: true, message: 'Nhập họ tên' }]} required>
                                            <Input placeholder="Nguyễn Văn A" value={data.name} onChange={e => setField("name", e.target.value)} size="large" style={{ borderRadius: 10 }} />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Divider orientation="left" plain style={{ margin: "20px 0 20px" }}><Space><Car size={16} color="#f59e0b" /><span style={{ fontSize: 12, fontWeight: 800, color: '#475569' }}>THÔNG TIN XE (TẤT CẢ BẮT BUỘC)</span></Space></Divider>
                                <Row gutter={16}>
                                    <Col span={8}>
                                        <Form.Item name="carBrand" label={<span style={{ fontWeight: 600, fontSize: 13 }}><Car size={14} color="#ef4444" style={{ marginRight: 8 }} />Hãng</span>} rules={[{ required: true, message: 'Chọn hãng' }]} required>
                                            <Select placeholder="Hãng xe" style={{ width: '100%' }} size="large" value={data.carBrand || undefined} onChange={v => setField("carBrand", v)} showSearch allowClear>{CAR_BRANDS.map(b => <Option key={b} value={b}>{b}</Option>)}</Select>
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item name="carYear" label={<span style={{ fontWeight: 600, fontSize: 13 }}><Calendar size={14} color="#8b5cf6" style={{ marginRight: 8 }} />Năm</span>} rules={[{ required: true, message: 'Năm sx' }]} required>
                                            <Select placeholder="Năm" style={{ width: '100%' }} size="large" value={data.carYear || undefined} onChange={v => setField("carYear", v)} allowClear>{Array.from({ length: 30 }, (_, i) => { const y = new Date().getFullYear() - i; return <Option key={y} value={String(y)}>{y}</Option>; })}</Select>
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item name="carModel" label={<span style={{ fontWeight: 600, fontSize: 13 }}><Info size={14} color="#06b6d4" style={{ marginRight: 8 }} />Dòng/Biển</span>} rules={[{ required: true, message: 'Nhập dòng/biển' }]} required>
                                            <Input placeholder="Vios - 30A..." value={data.carModel} onChange={e => setField("carModel", e.target.value)} size="large" style={{ borderRadius: 10 }} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={24}>
                                        <Form.Item name="carName" label={<span style={{ fontWeight: 600, fontSize: 13 }}><Car size={14} color="#f59e0b" style={{ marginRight: 8 }} />Tên xe</span>} rules={[{ required: true, message: 'Nhập tên xe' }]} required>
                                            <Input placeholder="Ví dụ: Toyota Vios 2022, Honda City..." value={data.carName} onChange={e => setField("carName", e.target.value)} size="large" style={{ borderRadius: 10 }} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>
                        ) : (
                            <div className="animate-in slide-in-from-right-2 duration-400">
                                <Divider orientation="left" plain style={{ margin: "0 0 20px" }}><Space><Building2 size={16} color="#ec4899" /><span style={{ fontSize: 12, fontWeight: 800, color: '#475569' }}>DỊCH VỤ (TẤT CẢ BẮT BUỘC)</span></Space></Divider>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item name="garageId" label={<span style={{ fontWeight: 600, fontSize: 13 }}><Building2 size={14} color="#3b82f6" style={{ marginRight: 8 }} />Hệ thống Garage</span>} rules={[{ required: true, message: 'Chọn hệ thống' }]} required>
                                            <Select placeholder="Chọn Garage" style={{ width: '100%' }} size="large" value={data.garageId || undefined} onChange={v => setField("garageId", v)}>{garages.map(g => <Option key={g.id} value={g.id}>{g.name}</Option>)}</Select>
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item name="serviceId" label={<span style={{ fontWeight: 600, fontSize: 13 }}><Wrench size={14} color="#10b981" style={{ marginRight: 8 }} />Loại dịch vụ</span>} rules={[{ required: true, message: 'Chọn dịch vụ' }]} required>
                                            <Select placeholder="Chọn yêu cầu dịch vụ" style={{ width: '100%' }} size="large" value={data.serviceId || undefined} onChange={v => setField("serviceId", v)}>{services.map(s => <Option key={s.id} value={s.id}>{s.name}</Option>)}</Select>
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Divider orientation="left" plain style={{ margin: "20px 0 20px" }}><Space><Calendar size={16} color="#dc2626" /><span style={{ fontSize: 12, fontWeight: 800, color: '#475569' }}>LỊCH HẸN & GHI CHÚ</span></Space></Divider>
                                <Row gutter={16}>
                                    <Col span={24}>
                                        {data.garageId && (
                                            <div>
                                                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 13 }}>
                                                    <MapPin size={14} color="#ef4444" style={{ marginRight: 8, display: 'inline-block' }} />
                                                    Chi nhánh <span className="text-red-500">*</span>
                                                </label>
                                                <Form.Item name="branchId" noStyle rules={[{ required: true, message: 'Chọn chi nhánh' }]}>
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                                        {branches.map(b => {
                                                            const isSelected = data.branchId === b.id;
                                                            return (
                                                                <div key={b.id}
                                                                    onClick={() => { setField('branchId', b.id); form.setFieldValue('branchId', b.id); }}
                                                                    style={{
                                                                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                                                                        padding: '10px',
                                                                        border: isSelected ? '2px solid #dc2626' : '1.5px solid #e2e8f0',
                                                                        borderRadius: 12,
                                                                        background: isSelected ? '#fff5f5' : '#fff',
                                                                        transition: 'all 0.2s',
                                                                        boxShadow: isSelected ? '0 4px 12px rgba(220,38,38,0.1)' : 'none'
                                                                    }}>
                                                                    {/* Thumbnail */}
                                                                    <div style={{ width: 40, height: 40, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: 'linear-gradient(135deg,#0f172a,#b91c1c)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                        {b.imageUrl
                                                                            ? <img src={normalizeImageUrl(b.imageUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                                                                            : <MapPin size={16} color="rgba(255,255,255,0.6)" />
                                                                        }
                                                                    </div>
                                                                    {/* Info */}
                                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                                        <div style={{ fontWeight: 700, fontSize: 11, color: isSelected ? '#dc2626' : '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</div>
                                                                        {b.address && <div style={{ fontSize: 10, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📍 {b.address}</div>}
                                                                    </div>
                                                                    {isSelected && (
                                                                        <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                                            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </Form.Item>
                                            </div>
                                        )}
                                    </Col>
                                    <Col span={6}>
                                        <Form.Item name="scheduleDate" label={<span style={{ fontWeight: 600, fontSize: 13 }}><Calendar size={14} color="#dc2626" style={{ marginRight: 8 }} />Ngày</span>} rules={[{ required: true, message: 'Chọn ngày' }]} required>
                                            <DatePicker style={{ width: '100%', borderRadius: 10 }} size="large" placeholder="Ngày" value={data.scheduleDate} onChange={d => setField("scheduleDate", d)} disabledDate={c => c && c < dayjs().startOf('day')} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={6}>
                                        <Form.Item name="scheduleTime" label={<span style={{ fontWeight: 600, fontSize: 13 }}><Clock size={14} color="#8b5cf6" style={{ marginRight: 8 }} />Giờ hẹn</span>} rules={[{ required: true, message: 'Chọn giờ' }]} required>
                                            <TimePicker
                                                size="large"
                                                style={{ width: '100%', borderRadius: 10 }}
                                                placeholder="Chọn giờ"
                                                format="HH:mm"
                                                value={data.scheduleTime ? dayjs(data.scheduleTime, 'HH:mm') : null}
                                                onChange={t => setField('scheduleTime', t ? t.format('HH:mm') : null)}
                                                minuteStep={30}
                                                hideDisabledOptions
                                                disabledTime={() => ({
                                                    disabledHours: () => [0, 1, 2, 3, 4, 5, 6, 7, 18, 19, 20, 21, 22, 23],
                                                    disabledMinutes: (selectedHour) => {
                                                        if (selectedHour === 17) return Array.from({ length: 30 }, (_, i) => i + 31);
                                                        return [];
                                                    }
                                                })}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Form.Item name="note" label={<span style={{ fontWeight: 600, fontSize: 13 }}><Info size={14} color="#6b7280" style={{ marginRight: 8 }} />Ghi chú tình trạng xe (Không bắt buộc)</span>}>
                                    <TextArea rows={2} placeholder="Ví dụ: Thay dầu, Kiểm tra máy..." value={data.note} onChange={e => setField("note", e.target.value)} style={{ borderRadius: 10 }} />
                                </Form.Item>
                            </div>
                        )}
                    </Form>
                </div>

                <div style={{ padding: "10px 40px 30px", background: "#f8fafc", borderTop: "1px solid #eef2f6", borderBottomLeftRadius: 24, borderBottomRightRadius: 24 }}>
                    <div style={{ marginBottom: 15 }}>
                        <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8' }}>TIẾN ĐỘ THÔNG TIN BẮT BUỘC:</span>
                            <span style={{ fontSize: 12, fontWeight: 900, color: '#b91c1c' }}>{calculateProgress()}%</span>
                        </div>
                        <Progress percent={calculateProgress()} showInfo={false} strokeColor="#b91c1c" trailColor="#e2e8f0" strokeWidth={6} />
                    </div>
                    {formError && (
                        <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, color: '#dc2626', fontSize: 13, fontWeight: 600, marginBottom: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 16 }}>⚠️</span> {formError}
                        </div>
                    )}
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                        <Button onClick={onClose} size="large" style={{ height: 48, borderRadius: 10, padding: '0 25px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Đóng</Button>
                        {step === 0 ? (
                            <Button type="primary" size="large" onClick={handleNext} style={{ height: 48, background: '#0f172a', borderColor: '#0f172a', borderRadius: 10, padding: '0 35px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>TIẾP THEO <ChevronRight size={18} /></Button>
                        ) : (
                            <>
                                <Button size="large" onClick={() => setStep(0)} style={{ height: 48, borderRadius: 10, padding: '0 20px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><ChevronLeft size={18} /> QUAY LẠI</Button>
                                <Button type="primary" size="large" onClick={handleSubmit} loading={submitting} style={{ height: 48, background: 'linear-gradient(135deg, #0f172a 0%, #b91c1c 100%)', borderColor: 'transparent', borderRadius: 10, padding: '0 30px', fontWeight: 900, fontSize: 13, textTransform: 'uppercase', boxShadow: '0 10px 20px -5px rgba(185,28,28,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    CHỐT THỜI GIAN SỬA
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </Modal>

            <BookingSuccessModal
                visible={showSuccess}
                onClose={() => setShowSuccess(false)}
                bookingData={lastBooking}
            />
        </>
    )
};
export default BookingModal;
